/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SpecTemplateClauses } from '@gh/builder-shared-data';
import { createDefaultGridModel } from '@gh/common-internal-shared';
import { EnumDictionary, getRouteInitData, TextTypes } from '@gh/common-shared';
import { FormMode, isEditableFormMode, STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildEditableTextColumn,
  FocusedCell,
  GridDefinition,
  GridDefinitionFactory,
  rowDragMoveEventArrayFactory,
} from '@gh/core-ui-grid';
import { assign } from 'lodash';
import { JobSpecTemplateDetailsBusinessService } from '../../../resources/job-spec-template-details-business.service';
import {
  getJstClauseId,
  JstClause,
  jstClauseArr,
  jstClauseFieldState,
  jstClauseValidateField,
  setJstClauseText,
} from '../../../resources/job-spec-template.model';

@Component({
  selector: 'gh-jst-section-clause-list',
  template: require('./jst-section-clause-list.component.html'),
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JstSectionClauseListComponent implements OnInit {
  @Input() clausesAre: SpecTemplateClauses;
  @Input() clauses: JstClause[] = [];
  @Input() mode: FormMode;
  @Input() focusedCell: FocusedCell;

  @Output() clausesAreChange = new EventEmitter<SpecTemplateClauses>();
  @Output() clausesChange = new EventEmitter<JstClause[]>();
  @Output() resetFocusedCell = new EventEmitter<void>();

  clausesAreDict: EnumDictionary<SpecTemplateClauses>;

  definition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private business: JobSpecTemplateDetailsBusinessService,
              private gridDefinitionFactory: GridDefinitionFactory) {

  }

  get editable(): boolean {
    return isEditableFormMode(this.mode);
  }

  ngOnInit(): void {
    assign(this, getRouteInitData(this.route));

    this.definition = this.buildGridDefinition();
  }

  onClausesAreChange(clausesAre: SpecTemplateClauses): void {
    this.clausesAreChange.emit(clausesAre);
  }

  onClauseAdd(): void {
    const newClauses = jstClauseArr.prepend(this.clauses, this.business.createNewClause());
    this.clausesChange.emit(newClauses);
  }

  onClauseRemove(clause: JstClause): void {
    const newClauses = jstClauseArr.remove(this.clauses, getJstClauseId(clause));
    this.clausesChange.emit(newClauses);
  }

  onToggleTextWarning(clause: JstClause): void {
    const newClauses = jstClauseArr.merge(
      this.clauses,
      getJstClauseId(clause),
      { isTextWarning: !clause.isTextWarning });
    this.clausesChange.emit(newClauses);
  }

  onFocusedCellChange(): void {
    this.resetFocusedCell.emit();
  }

  private moveClauses(from: JstClause, to: JstClause): void {
    const fromIndex = jstClauseArr.indexOf(this.clauses, getJstClauseId(from));
    const toIndex = jstClauseArr.indexOf(this.clauses, getJstClauseId(to));

    this.clausesChange.emit(jstClauseArr.move(this.clauses, fromIndex, toIndex));
  }

  private setClauseName(clause: JstClause, value: string): void {
    const newClauses = jstClauseArr.merge(
      this.clauses,
      getJstClauseId(clause),
      // 1. It is important to generate only the smallest subentity for merge operation,
      // because entity clause may be not the actual one.
      // State of isTextWarning flag can be different from the latest one.
      // 2. We always should init entityState as undefined in order to reset errors on merge
      // if entity becomes valid
      setJstClauseText({ entityState: void 0 }, 'text', value));
    this.clausesChange.emit(newClauses);
  }

  private buildGridDefinition(): GridDefinition {
    const model = createDefaultGridModel({
      editable: this.editable,
      getId: getJstClauseId,
      getCellDef: jstClauseFieldState,
      validate: jstClauseValidateField,
      command: (entity: JstClause, _, value) => this.setClauseName(entity, value),
    });
    return this.gridDefinitionFactory.buildManualWithModel({
      model,
      columns: [{
        rowDrag: this.editable,
        field: 'text',
        width: 200,
        ...buildEditableTextColumn(TextTypes.Unlimited, model.getCellAccessor('text')),
      }, {
        field: 'isTextWarning',
        width: 200,
      }],
      options: <any>{
        animateRows: true,
        onRowDragMove: rowDragMoveEventArrayFactory(this.moveClauses.bind(this)),
      },
    });
  }
}
